// 초안 잇기 — 이 기기의 초안첩(`drafts.ts`)과 서버(`/v1/drafts`)를 맞춘다.
//
// ## 규칙
// - 글자는 먼저 이 기기에 적고, 서버로는 **디바운스**해서 올린다. 글자마다 올리면 쓰기 상한(테넌트당
//   초당 20)을 혼자 다 먹는다.
// - 비우거나 보내면 **바로** 지운다. 늦게 지우면 그 사이 다른 기기가 보낸 글을 초안으로 되살린다.
// - 받을 때는 칸마다 `updated_at` 이 늦은 쪽이 이긴다. 서버에 없는 칸은 둘 중 하나다 — 서버와 맞춘 적이
//   있으면 다른 기기가 보냈거나 지운 것이니 이 기기에서도 지우고, 맞춘 적이 없으면(오프라인에서 쓴 글)
//   올린다.
// - 사건(`draft_changed`)은 내 기기들에게 온다. `origin` 이 내 꼬리표면 내가 쓴 것이니 버린다.

import type { DraftWrite, ServerDraft } from '@maru/client'
import type { ChannelId, Event, Seq } from '@maru/protocol'
import { type DraftBook, type DraftKey, thread_draft } from './drafts.ts'

/** 우리가 쓰는 만큼의 API. 시험은 가짜를 넣는다. */
export interface DraftApi {
  drafts(): Promise<ServerDraft[]>
  draft_put(channel: ChannelId, write: DraftWrite): Promise<ServerDraft | undefined>
  draft_drop(channel: ChannelId, thread?: Seq | null, origin?: string): Promise<void>
}

/** 디바운스 간격(ms). */
export const DRAFT_DEBOUNCE = 800

/** 칸 열쇠 → (채널, 뿌리). */
export function spot_of(key: DraftKey): { channel: ChannelId; thread: Seq | null } {
  if (typeof key === 'number') return { channel: key, thread: null }
  const [c, r] = key.split(':')
  return { channel: Number(c) as ChannelId, thread: Number(r) as Seq }
}

function key_of(d: { channel: ChannelId; thread?: Seq | null }): DraftKey {
  return d.thread === null || d.thread === undefined ? d.channel : thread_draft(d.channel, d.thread)
}

/** 이 기기 탭의 꼬리표. 영숫자·`-` 만(서버 규칙). */
export function fresh_origin(): string {
  return `d-${Math.random().toString(36).slice(2, 12)}`
}

export class DraftSync {
  readonly #book: DraftBook
  /** 화면이 읽는 초안첩. 쓰기는 [`edit`] 으로만 — 직접 쓰면 서버로 안 간다. */
  get book(): DraftBook {
    return this.#book
  }
  readonly #api: DraftApi
  readonly origin: string
  readonly #delay: number
  readonly #timers = new Map<DraftKey, ReturnType<typeof setTimeout>>()
  /** 받은 뒤 화면이 다시 읽게 부른다(사이드바 연필 등). */
  #changed: () => void = () => {}

  constructor(book: DraftBook, api: DraftApi, origin = fresh_origin(), delay = DRAFT_DEBOUNCE) {
    this.#book = book
    this.#api = api
    this.origin = origin
    this.#delay = delay
  }

  on_change(then: () => void): void {
    this.#changed = then
  }

  /** 입력칸이 바뀌었다. 이 기기에는 바로, 서버에는 늦게. 비었으면 서버에서도 바로 지운다. */
  edit(key: DraftKey, text: string, now = Date.now()): void {
    this.#book.put(key, text, now)
    clearTimeout(this.#timers.get(key))
    this.#timers.delete(key)
    if (text.trim() === '') {
      void this.#drop(key)
      return
    }
    this.#timers.set(
      key,
      setTimeout(() => {
        this.#timers.delete(key)
        void this.#push(key)
      }, this.#delay),
    )
  }

  /** 보냈다. 이 기기와 서버에서 바로 지운다. */
  sent(key: DraftKey): void {
    this.edit(key, '')
  }

  /** 기다리는 올리기를 지금 한다(창을 닫기 전 등). */
  async flush(): Promise<void> {
    const keys = [...this.#timers.keys()]
    for (const k of keys) clearTimeout(this.#timers.get(k))
    this.#timers.clear()
    await Promise.all(keys.map((k) => this.#push(k)))
  }

  /** 서버에서 받아 맞춘다. */
  async pull(): Promise<void> {
    let rows: ServerDraft[]
    try {
      rows = await this.#api.drafts()
    } catch {
      return // 초안은 편의다 — 다음 기회에.
    }
    const there = new Map(rows.map((r) => [key_of(r), r]))
    for (const [key, row] of there) {
      if (this.#timers.has(key)) continue // 지금 치는 중인 칸은 이 기기가 늦다.
      const at = Date.parse(row.updated_at)
      const mine = this.#book.of(key)
      const stamp = this.#book.stamp(key)
      if (mine === '' || at >= stamp.at) {
        if (mine !== row.body || !stamp.synced) this.#book.put(key, row.body, at, true)
      } else {
        void this.#push(key) // 이 기기 것이 더 늦다.
      }
    }
    for (const key of this.#book.all().keys()) {
      if (there.has(key) || this.#timers.has(key)) continue
      if (this.#book.stamp(key).synced) this.#book.put(key, '')
      else void this.#push(key)
    }
    this.#changed()
  }

  /** 소켓 사건. 다른 기기에서 바뀌었으면 받는다. */
  on_event(e: Event): void {
    if (e.t !== 'draft_changed') return
    if (e.origin === this.origin) return
    void this.pull()
  }

  async #push(key: DraftKey): Promise<void> {
    const body = this.#book.of(key)
    if (body === '') return
    const { channel, thread } = spot_of(key)
    const at = this.#book.stamp(key).at
    try {
      const won = await this.#api.draft_put(channel, {
        thread,
        body,
        updated_at: new Date(at === 0 ? Date.now() : at).toISOString(),
        origin: this.origin,
      })
      if (this.#book.of(key) !== body) return // 그 사이 더 쳤다.
      if (won === undefined) return
      if (won.body === body) this.#book.mark_synced(key)
      else {
        this.#book.put(key, won.body, Date.parse(won.updated_at), true)
        this.#changed()
      }
    } catch {
      // 못 올렸다. 이 기기에는 남아 있고, 맞춘 적 없음으로 남아 다음 받기에서 다시 올린다.
    }
  }

  async #drop(key: DraftKey): Promise<void> {
    const { channel, thread } = spot_of(key)
    try {
      await this.#api.draft_drop(channel, thread, this.origin)
    } catch {
      // 다음 받기에서 서버 것이 더 늦으면 되살아난다 — 초안은 약속이 아니다.
    }
  }
}
